import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTallerListo } from './useTallerListo';
import {
    fetchServiceContracts,
    fetchServiceVisits,
    fetchServiceRequests,
    fetchVisitHistory,
    addServiceContractDoc,
    updateServiceContractDoc,
    deleteServiceContractDoc,
    addServiceVisitDoc,
    updateServiceVisitDoc,
    deleteServiceVisitDoc,
    reorderServiceVisitDocs,
    addServiceRequestDoc,
    updateServiceRequestDoc,
    deleteServiceRequestDoc,
    fetchContractInvoices,
    addContractInvoiceDocs,
    updateContractInvoiceDoc,
    deleteContractInvoiceDoc,
} from '../services/database';
import type { ServiceContract, ServiceVisit, ServiceRequest, ContractInvoice } from '../types';

// ─── Query Keys ────────────────────────────────────────
/**
 * El taller va DENTRO de la clave.
 *
 * Estas cuatro consultas se cacheaban bajo `['serviceContracts']` a secas, sin
 * taller y sin `enabled`: se disparaban antes de que WorkspaceContext
 * resolviera, el lector caía en el respaldo 'default', y la lista vacía de un
 * taller que no existe quedaba guardada como éxito bajo una clave que ningún
 * cambio de taller puede invalidar. De ahí leen el Panel, el Calendario,
 * Finanzas y Servicios: los cuatro se quedaban en blanco a la vez.
 *
 * `PREFIJOS` son las mismas claves sin el taller, para invalidar desde una
 * mutación que no sabe en cuál está. React Query casa por PREFIJO mientras no
 * se pida `exact: true`, así que `['serviceContracts']` alcanza a
 * `['serviceContracts', 'ws-1']` — comprobado en useServiceQueries.test.tsx,
 * porque de esto depende que la lista se refresque tras crear un contrato.
 */
const KEYS = {
    contracts: (wsId: string) => ['serviceContracts', wsId] as const,
    visits: (wsId: string) => ['serviceVisits', wsId] as const,
    requests: (wsId: string) => ['serviceRequests', wsId] as const,
    contractInvoices: (wsId: string) => ['contractInvoices', wsId] as const,
};

const PREFIJOS = {
    contracts: ['serviceContracts'] as const,
    visits: ['serviceVisits'] as const,
    requests: ['serviceRequests'] as const,
    contractInvoices: ['contractInvoices'] as const,
};

// La puerta de arranque (`useTallerListo`) vive en hooks/useTallerListo.ts:
// sin taller resuelto no se consulta, en vez de consultar el respaldo.

// ─── Queries ───────────────────────────────────────────

export function useContracts() {
    const { wsId, listo } = useTallerListo();
    return useQuery({
        queryKey: KEYS.contracts(wsId),
        queryFn: () => fetchServiceContracts(wsId),
        enabled: listo,
    });
}

export function useVisits() {
    const { wsId, listo } = useTallerListo();
    return useQuery({
        queryKey: KEYS.visits(wsId),
        queryFn: () => fetchServiceVisits(wsId),
        enabled: listo,
    });
}

export function useServiceRequests() {
    const { wsId, listo } = useTallerListo();
    return useQuery({
        queryKey: KEYS.requests(wsId),
        queryFn: () => fetchServiceRequests(wsId),
        enabled: listo,
    });
}

// ─── Contract Mutations ────────────────────────────────

export function useAddContract() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: Omit<ServiceContract, 'id'>) => addServiceContractDoc(data),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.contracts }),
    });
}

export function useUpdateContract() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<ServiceContract> }) =>
            updateServiceContractDoc(id, updates),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.contracts }),
    });
}

export function useDeleteContract() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteServiceContractDoc(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.contracts }),
    });
}

// ─── Visit Mutations ───────────────────────────────────

export function useAddVisit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: Omit<ServiceVisit, 'id'>) => addServiceVisitDoc(data),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.visits }),
    });
}

export function useUpdateVisit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<ServiceVisit> }) =>
            updateServiceVisitDoc(id, updates),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.visits }),
    });
}

export function useDeleteVisit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteServiceVisitDoc(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.visits }),
    });
}

export function useReorderVisits() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (visitIds: string[]) => reorderServiceVisitDocs(visitIds),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.visits }),
    });
}

// ─── Request Mutations ─────────────────────────────────

export function useAddServiceRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data: Omit<ServiceRequest, 'id'>) => addServiceRequestDoc(data),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.requests }),
    });
}

export function useUpdateServiceRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<ServiceRequest> }) =>
            updateServiceRequestDoc(id, updates),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.requests }),
    });
}

export function useDeleteServiceRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteServiceRequestDoc(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.requests }),
    });
}


// ─── Contract Invoices (monthly billing) ───────────────

export function useContractInvoices() {
    const { wsId, listo } = useTallerListo();
    return useQuery<ContractInvoice[]>({
        queryKey: KEYS.contractInvoices(wsId),
        queryFn: () => fetchContractInvoices(wsId),
        enabled: listo,
    });
}

/** Bulk-create a month's invoices. Returns how many rows were actually written —
 *  duplicates are ignored by the DB constraint, so 0 means "already generated". */
export function useGenerateContractInvoices() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (rows: Omit<ContractInvoice, 'id' | 'status'>[]) => addContractInvoiceDocs(rows),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.contractInvoices }),
    });
}

export function useUpdateContractInvoice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<ContractInvoice> }) =>
            updateContractInvoiceDoc(id, updates),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.contractInvoices }),
    });
}

export function useDeleteContractInvoice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteContractInvoiceDoc(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: PREFIJOS.contractInvoices }),
    });
}
